<template>
  <div class="shape-game-wrapper">
    <LevelComplete
      v-if="showLevelComplete"
      class="level-complete-screen"
    />

    <div
      v-else
      class="shape-gathering-game"
    >
      <!-- Shape containers -->
      <div class="shape-targets">
        <div
          v-for="(shape, index) in shapesList"
          :key="index"
          class="shape-target"
          :class="shape.toLowerCase() + '-target'"
          :data-shape="shape"
          :style="getTargetShapeStyle(shape)"
          @dragover.prevent
          @drop="handleDrop($event, shape)"
          @click="speakOutLoud(shape)"
        >
          <h2 class="shape-label">
            {{ shape }}
          </h2>

          <div class="dropped-items">
            <span
              v-for="item in correctDrops[shape]"
              :key="item.id"
            >
              {{ item.emoji }}
            </span>
          </div>
        </div>
      </div>

      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>

      <!-- Draggable emojis -->
      <div
        ref="draggableContainer"
        class="draggable-items"
      >
        <div
          v-for="item in draggableItems"
          :key="item.id"
          class="draggable-emoji"
          :style="getDraggableStyle(item)"
          draggable="true"
          @dragstart="handleDrag($event, item)"
          @click="speakOutLoud(item.emoji)"
          @touchstart="handleTouchStart($event, item)"
          @touchmove.prevent="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          {{ item.emoji }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin.js";

const REDIRECT_ROUTE = "/shapes";
const LEVEL_COMPLETE_DURATION = 5000;
const MESSAGE_DURATION = 2000;
const SUCCESS_SOUND_URL =
  "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a";

export default {
  name: "ShapeGatheringGame",
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    const shapesList = [
      "Circle",
      "Square",
      "Triangle",
      "Rectangle",
      "Pentagon",
    ];

    const shapeEmojis = [
      // Circle shapes
      { id: 1, emoji: "⚽", shape: "Circle" },
      { id: 2, emoji: "🥇", shape: "Circle" },
      { id: 3, emoji: "🍎", shape: "Circle" },
      { id: 4, emoji: "🌕", shape: "Circle" },

      // Square shapes
      { id: 5, emoji: "📕", shape: "Square" },
      { id: 6, emoji: "🎁", shape: "Square" },
      { id: 7, emoji: "🖼️", shape: "Square" },

      // Triangle shapes
      { id: 8, emoji: "🌲", shape: "Triangle" },
      { id: 9, emoji: "📐", shape: "Triangle" },
      { id: 10, emoji: "🍕", shape: "Triangle" },

      // Rectangle shapes
      { id: 11, emoji: "📱", shape: "Rectangle" },
      { id: 12, emoji: "⌨️", shape: "Rectangle" },
      { id: 13, emoji: "💳", shape: "Rectangle" },

      // Pentagon shapes
      { id: 14, emoji: "🏠", shape: "Pentagon" },
      { id: 15, emoji: "💎", shape: "Pentagon" },
      { id: 16, emoji: "🛡️", shape: "Pentagon" },
    ];

    const correctDrops = shapesList.reduce((acc, shape) => {
      acc[shape] = [];
      return acc;
    }, {});

    return {
      shapesList,
      shapeEmojis,
      correctDrops,
      draggableItems: [],
      message: "",
      touchOffsetX: 0,
      touchOffsetY: 0,
      draggedItem: null,
      draggedStyle: {},
      selectedVoice: null,

      showLevelComplete: false,
      isCompleting: false,
      messageTimeoutId: null,
      redirectTimeoutId: null,
      voicesChangedHandler: null,
    };
  },

  mounted() {
    this.initializeGame();
    this.initializeVoices();
  },

  beforeUnmount() {
    this.cleanup();
  },
  methods: {
    initializeGame() {
      const shuffledItems = this.shuffleArray([...this.shapeEmojis]);

      this.draggableItems = shuffledItems.map((item) => ({
        ...item,
        position: {
          left: `${Math.floor(Math.random() * 75) + 5}%`,
          top: `${Math.floor(Math.random() * 70) + 5}%`,
        },
      }));
    },

    initializeVoices() {
      if (typeof speechSynthesis === "undefined") {
        console.warn("Speech Synthesis API not supported in this browser.");
        return;
      }

      if (typeof this.setupVoices === "function") {
        this.setupVoices();
      }

      this.voicesChangedHandler = () => {
        if (typeof this.setupVoices === "function") {
          this.setupVoices();
        }
      };

      if (typeof speechSynthesis.addEventListener === "function") {
        speechSynthesis.addEventListener(
          "voiceschanged",
          this.voicesChangedHandler
        );
      } else {
        speechSynthesis.onvoiceschanged = this.voicesChangedHandler;
      }
    },

    cleanup() {
      if (this.messageTimeoutId) {
        clearTimeout(this.messageTimeoutId);
        this.messageTimeoutId = null;
      }

      if (this.redirectTimeoutId) {
        clearTimeout(this.redirectTimeoutId);
        this.redirectTimeoutId = null;
      }

      if (
        typeof speechSynthesis !== "undefined" &&
        this.voicesChangedHandler
      ) {
        if (typeof speechSynthesis.removeEventListener === "function") {
          speechSynthesis.removeEventListener(
            "voiceschanged",
            this.voicesChangedHandler
          );
        } else if (
          speechSynthesis.onvoiceschanged === this.voicesChangedHandler
        ) {
          speechSynthesis.onvoiceschanged = null;
        }
      }
    },

    shuffleArray(array) {
      for (let i = array.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    },

    getDraggableStyle(item) {
      if (this.draggedItem && this.draggedItem.id === item.id) {
        return this.draggedStyle;
      }

      return {
        position: "absolute",
        left: item.position.left,
        top: item.position.top,
      };
    },

    handleDrag(event, item) {
      if (this.isCompleting) return;

      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", JSON.stringify(item));
      }
    },

    getDraggedItemFromEvent(event) {
      try {
        const itemData = event?.dataTransfer?.getData("text/plain");

        if (!itemData) return null;

        return JSON.parse(itemData);
      } catch (error) {
        console.error("Failed to parse dragged item:", error);
        return null;
      }
    },

    handleDrop(event, targetShape) {
      if (this.isCompleting) return;

      const item = this.getDraggedItemFromEvent(event);

      if (!item) {
        this.setMessage("❌ Try again!");
        return;
      }

      if (item.shape !== targetShape) {
        this.setMessage("❌ Try again!");
        this.safeSpeak("Try again!");
        return;
      }

      const alreadyPlaced = this.correctDrops[targetShape].some(
        (placedItem) => placedItem.id === item.id
      );

      if (alreadyPlaced) {
        this.setMessage("Already placed!");
        return;
      }

      this.correctDrops[targetShape].push(item);
      this.draggableItems = this.draggableItems.filter(
        (draggableItem) => draggableItem.id !== item.id
      );

      this.playSound(SUCCESS_SOUND_URL);
      this.clearMessage();
      this.checkLevelComplete();
    },

    checkLevelComplete() {
      if (this.isCompleting) return;

      const totalCorrect = this.shapesList.reduce((total, shape) => {
        return total + this.correctDrops[shape].length;
      }, 0);

      if (totalCorrect !== this.shapeEmojis.length) return;

      this.isCompleting = true;
      this.clearMessage();
      this.safeSpeak("Great Job!");
      this.showLevelComplete = true;

      this.redirectTimeoutId = window.setTimeout(() => {
        this.$router.push(REDIRECT_ROUTE);
      }, LEVEL_COMPLETE_DURATION);
    },

    handleTouchStart(event, item) {
      if (this.isCompleting) return;
      if (!event.touches || !event.touches.length) return;
      if (!this.$refs.draggableContainer) return;

      const touch = event.touches[0];
      const rect = event.currentTarget.getBoundingClientRect();
      const parentRect = this.$refs.draggableContainer.getBoundingClientRect();

      this.draggedItem = item;
      this.touchOffsetX = touch.clientX - rect.left;
      this.touchOffsetY = touch.clientY - rect.top;

      this.draggedStyle = {
        position: "absolute",
        left: `${rect.left - parentRect.left}px`,
        top: `${rect.top - parentRect.top}px`,
        zIndex: 1000,
      };
    },

    handleTouchMove(event) {
      if (this.isCompleting) return;
      if (!this.draggedItem) return;
      if (!event.touches || !event.touches.length) return;
      if (!this.$refs.draggableContainer) return;

      const touch = event.touches[0];
      const parentRect = this.$refs.draggableContainer.getBoundingClientRect();

      this.draggedStyle = {
        ...this.draggedStyle,
        left: `${touch.clientX - this.touchOffsetX - parentRect.left}px`,
        top: `${touch.clientY - this.touchOffsetY - parentRect.top}px`,
      };
    },

    handleTouchEnd(event) {
      if (this.isCompleting) {
        this.resetDragState();
        return;
      }

      if (!this.draggedItem || !event.changedTouches || !event.changedTouches.length) {
        this.resetDragState();
        return;
      }

      const touch = event.changedTouches[0];
      const dropZones = this.$el.querySelectorAll(".shape-target");
      let dropped = false;

      for (const dropZone of dropZones) {
        const rect = dropZone.getBoundingClientRect();

        const isInsideZone =
          touch.clientX >= rect.left &&
          touch.clientX <= rect.right &&
          touch.clientY >= rect.top &&
          touch.clientY <= rect.bottom;

        if (isInsideZone) {
          const targetShape = dropZone.dataset.shape;

          if (targetShape) {
            this.handleDrop(
              {
                dataTransfer: {
                  getData: () => JSON.stringify(this.draggedItem),
                },
              },
              targetShape
            );
            dropped = true;
          }
          break;
        }
      }

      if (!dropped) {
        this.setMessage("❌ Try again!");
      }

      this.resetDragState();
    },

    resetDragState() {
      this.draggedItem = null;
      this.draggedStyle = {};
    },

    setMessage(text) {
      this.message = text;

      if (this.messageTimeoutId) {
        clearTimeout(this.messageTimeoutId);
      }

      this.messageTimeoutId = window.setTimeout(() => {
        this.message = "";
        this.messageTimeoutId = null;
      }, MESSAGE_DURATION);
    },

    clearMessage() {
      this.message = "";

      if (this.messageTimeoutId) {
        clearTimeout(this.messageTimeoutId);
        this.messageTimeoutId = null;
      }
    },

    safeSpeak(text) {
      if (typeof this.speakOutLoud === "function") {
        this.speakOutLoud(text);
      }
    },

    playSound(url) {
      const audio = new Audio(url);
      audio.play().catch((error) => {
        console.warn("Audio playback was blocked or failed:", error);
      });
    },

    getTargetShapeStyle(shape) {
      const style = {};

      if (shape === "Circle") {
        style.borderRadius = "50%";
      } else if (shape === "Rectangle") {
        style.width = "150px";
      } else if (shape === "Pentagon") {
        style.width = "100px";
        style.height = "100px";
        style.clipPath =
          "polygon(50% 0%, 100% 38%, 81% 100%, 19% 100%, 0% 38%)";
      } else if (shape === "Triangle") {
        style.width = "100px";
        style.height = "100px";
        style.clipPath = "polygon(50% 0%, 0% 100%, 100% 100%)";
      }

      return style;
    },
  },
};
</script>

<style scoped>
.shape-game-wrapper {
  width: 100%;
  min-height: 100vh;
}

.level-complete-screen {
  width: 100%;
  min-height: 100vh;
}

.shape-gathering-game {
  position: relative;
  width: 100%;
  min-height: 100vh;
  padding: 1rem;
  box-sizing: border-box;
}

/* Shape targets */
.shape-targets {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.shape-target {
  width: 100px;
  height: 100px;
  background-color: lightgray;
  border-radius: 10px;
  position: relative;
  text-align: center;
  padding-top: 10px;
  cursor: pointer;
  box-sizing: border-box;
}

.shape-target .shape-label {
  position: absolute;
  width: 100%;
  text-align: center;
  top: 30px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 1rem;
  font-weight: bold;
  color: black;
  text-transform: capitalize;
  pointer-events: none;
}

/* Triangle shape target */
.triangle-target {
  width: 100px;
  height: 100px;
  background-color: lightgray;
  clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  position: relative;
}

.triangle-target .shape-label {
  top: 35px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
}

/* Pentagon shape target */
.pentagon-target {
  clip-path: polygon(50% 0%, 100% 38%, 81% 100%, 19% 100%, 0% 38%);
  width: 100px;
  height: 100px;
  position: relative;
}

/* Rectangle shape target */
.rectangle-target {
  width: 150px;
}

.dropped-items {
  position: absolute;
  top: 10px;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: auto;
  padding: 5px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-content: start;
  box-sizing: border-box;
}

.dropped-items span {
  font-size: 2rem;
  margin: 2px;
}

/* Messages */
.message {
  text-align: center;
  font-size: 2rem;
  font-weight: bold;
  margin: 1rem 0;
}

/* Draggable items */
.draggable-items {
  position: relative;
  margin: auto;
  width: 80%;
  height: 55vh;
  min-height: 320px;
}

.draggable-emoji {
  cursor: grab;
  font-size: 5rem;
  position: absolute;
  transition: transform 0.3s;
  user-select: none;
  -webkit-user-select: none;
  touch-action: none;
}

.draggable-emoji:hover {
  transform: scale(1.2);
}

@media (hover: none) {
  .draggable-emoji:hover {
    transform: none;
  }
}
</style>